import { useEffect, useMemo } from 'react';

import { UseBaseTable } from 'hooks/useBaseTable';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useQueryState } from 'hooks/useQueryState';
import { useQuerystring } from 'hooks/useQuerystring';

import { FilterNames } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import {
    ACTIVITY_TYPE_FILTERS,
    ALL_FILTER_BUTTON,
    TIME_FILTERS,
    timeFilterInitialState,
} from 'components/widgets/KeyAchievements/keyAchievements.consts';
import { UseKeyAchievementsProvider } from 'components/widgets/KeyAchievements/keyAchievements.provider';

interface Props {
    ui: UseKeyAchievementsProvider['ui'];
    api: UseKeyAchievementsProvider['api'];
    title: string;
    subTitle: string;
    table: UseBaseTable['table'];
}

export const useKeyAchievementsLogic = ({ ui, api }: Props) => {
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { addQueryKey, getQueryKey } = useQuerystring();
    const [tableTimeFilterUrl, setTableTimeFilterUrl] = useQueryState('tableTimeFilter');

    const selectedActivityTypeFilter =
        ACTIVITY_TYPE_FILTERS.find((filter) => filter.value === ui?.state.activityType) || ALL_FILTER_BUTTON;

    const tableData = useMemo(() => {
        return api.getKeyAchievementsData.instance.data &&
            api.getKeyAchievementsData.instance.data.periodItemsList &&
            api.getKeyAchievementsData.instance.data.periodItemsList.length > 0
            ? api.getKeyAchievementsData.instance.data.periodItemsList?.[0].itemsList
            : [];
    }, [api.getKeyAchievementsData.instance.data]);

    const handleLastFilterClick = (filter: ISelectOption<string>) => {
        const value = filter.value;
        sendEventWithDimensions({
            category: 'Key Achievements',
            action: 'Range Selection',
            label: value,
        });
        setTableTimeFilterUrl(value);
    };

    const timePeriodFilterValue = TIME_FILTERS.find((filter) => filter.value === ui.state.timePeriod);

    const handleActivityTypeFilterClick = (filter: ISelectOption<string>) => {
        const value = filter.value;
        sendEventWithDimensions({
            category: 'Key Achievements',
            action: 'Range Selection',
            label: value,
        });

        addQueryKey({ key: FilterNames.activity_type, value });
    };

    useEffect(() => {
        if (!tableTimeFilterUrl && !ui.isInPinboard) {
            setTableTimeFilterUrl(timeFilterInitialState);
        }
    }, [tableTimeFilterUrl, ui.isInPinboard]);

    useEffect(() => {
        if (!getQueryKey({ key: FilterNames.activity_type }) && !ui.isInPinboard) {
            addQueryKey({
                key: FilterNames.activity_type,
                value: ACTIVITY_TYPE_FILTERS[0].value,
            });
        }
    }, [getQueryKey, addQueryKey]);

    return {
        logic: {
            selectedActivityTypeFilter,
            tableData,
            handleActivityTypeFilterClick,
            handleLastFilterClick,
            timePeriodFilterValue,
        },
    };
};
